// src/utils/elk.js — ELK layout engine with overlay and arrange grouping

import ELK from "elkjs/lib/elk.bundled.js";
import { NODE_WIDTH, NODE_HEIGHT } from "./constants";

const elk = new ELK();

const DIRECTION_MAP = {
  TB: "DOWN",
  BT: "UP",
  LR: "RIGHT",
  RL: "LEFT",
};

const GROUP_PADDING = 30;

// Estimate the *real* rendered footprint of a node so ELK reserves enough room
// and layers never overlap. Three shapes exist:
//   • service-scenario image nodes (data.iconType): a square image (per-node
//     data.width/height, often resized) PLUS a wide caption rendered BELOW it.
//     The caption is absolutely positioned, so it visually hangs into the gap
//     beneath the image — we must add its height (and width) to the box.
//   • Icons mode: a small ~40px icon with a short caption underneath.
//   • Everything else (Node / Hybrid / Service cards): a wide card with an
//     ID/description column and an RTO panel (~180×50 rendered).
const LABEL_LINE_H = 14;
const LABEL_CHAR_W = 6;

function measureNode(node, fallbackW, fallbackH) {
  const d = node.data || {};
  const label = String(d.label || "");

  if (d.iconType) {
    const imgW = Number(d.width) || 80;
    const imgH = Number(d.height) || 80;
    const fullLabelW = label.length * LABEL_CHAR_W;
    const labelBoxW = Math.min(Math.max(fullLabelW, imgW), 170);
    const lines = Math.max(1, Math.ceil(fullLabelW / Math.max(labelBoxW, 60)));
    return {
      w: Math.max(imgW, labelBoxW),
      h: imgH + 8 + lines * LABEL_LINE_H,
    };
  }

  if (d.nodeMode === "Icons") {
    const labelW = Math.min(Math.max(label.length * 5, 48), 80);
    return { w: labelW, h: 44 + 2 * LABEL_LINE_H };
  }

  // Card modes — fixed footprint regardless of label length.
  return { w: fallbackW, h: fallbackH };
}

/**
 * Final safety net: ELK occasionally leaves residual overlaps (connected-component
 * packing, or a user-placed node that kept its drop position landing on an ELK
 * slot). Nudge any genuinely-overlapping nodes apart. Nodes sharing a layer (similar
 * y) are separated horizontally to preserve the top-down layered look; nodes in
 * different layers are separated vertically.
 */
function resolveOverlaps(layoutedNodes, gap = 16) {
  const boxes = layoutedNodes.map((n) => ({
    n,
    x: n.position.x,
    y: n.position.y,
    w: n.width,
    h: n.height,
  }));

  const PASSES = 8;
  for (let pass = 0; pass < PASSES; pass++) {
    let moved = false;
    for (let i = 0; i < boxes.length; i++) {
      for (let j = i + 1; j < boxes.length; j++) {
        const a = boxes[i];
        const b = boxes[j];
        const ox = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x) + gap;
        const oy = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y) + gap;
        if (ox <= 0 || oy <= 0) continue; // not overlapping

        const sameLayer = Math.abs(a.y - b.y) < Math.max(a.h, b.h) * 0.5;
        if (sameLayer) {
          const shift = ox / 2;
          if (a.x <= b.x) { a.x -= shift; b.x += shift; }
          else { a.x += shift; b.x -= shift; }
        } else {
          const shift = oy / 2;
          if (a.y <= b.y) { a.y -= shift; b.y += shift; }
          else { a.y += shift; b.y -= shift; }
        }
        moved = true;
      }
    }
    if (!moved) break;
  }

  boxes.forEach((box) => {
    box.n.position = { x: box.x, y: box.y };
  });
  return layoutedNodes;
}

const GROUP_COLOURS = [
  { bg: "rgba(147, 197, 253, 0.10)", border: "rgba(147, 197, 253, 0.5)",  label: "#3b82f6" },  // blue
  { bg: "rgba(167, 243, 208, 0.10)", border: "rgba(167, 243, 208, 0.5)",  label: "#10b981" },  // green
  { bg: "rgba(253, 186, 116, 0.10)", border: "rgba(253, 186, 116, 0.5)",  label: "#f97316" },  // orange
  { bg: "rgba(196, 181, 253, 0.10)", border: "rgba(196, 181, 253, 0.5)",  label: "#8b5cf6" },  // purple
  { bg: "rgba(252, 165, 165, 0.10)", border: "rgba(252, 165, 165, 0.5)",  label: "#ef4444" },  // red
  { bg: "rgba(253, 224, 71, 0.08)",  border: "rgba(253, 224, 71, 0.45)",  label: "#ca8a04" },  // yellow
  { bg: "rgba(165, 243, 252, 0.10)", border: "rgba(165, 243, 252, 0.5)",  label: "#06b6d4" },  // cyan
  { bg: "rgba(251, 191, 236, 0.10)", border: "rgba(251, 191, 236, 0.5)",  label: "#ec4899" },  // pink
];

export { GROUP_COLOURS };

/**
 * Computes an ELK layout for React Flow nodes and edges.
 *
 * groupBy values:
 *   "none"              — flat layout, no groups
 *   "location" / "layer" — flat layout; groups computed as overlays after
 *   "location-arrange" / "layer-arrange" — compound ELK layout that moves nodes into groups
 */
export async function getLayoutedElements(
  nodes,
  edges,
  direction = "BT",
  vSpacing = 1,
  hSpacing = 1,
  groupBy = "none",
  nodeW = NODE_WIDTH,
  nodeH = NODE_HEIGHT
) {
  const elkDirection = DIRECTION_MAP[direction] || "UP";
  const layerGap = Math.max(20, vSpacing * 25);
  const nodeGap = Math.max(10, hSpacing * 20);

  const baseLayoutOptions = {
    "elk.algorithm": "layered",
    "elk.direction": elkDirection,
    "elk.layered.spacing.nodeNodeBetweenLayers": String(layerGap),
    "elk.spacing.nodeNode": String(nodeGap),
    "elk.layered.spacing.edgeNodeBetweenLayers": "10",
    "elk.layered.spacing.edgeEdgeBetweenLayers": "5",
    "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
    "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
    "elk.layered.crossingMinimization.greedySwitch.type": "TWO_SIDED",
    "elk.layered.considerModelOrder.strategy": "NODES_AND_EDGES",
    "elk.alignment": "CENTER",
    "elk.layered.nodePlacement.bk.fixedAlignment": "BALANCED",
    "elk.layered.compaction.postCompaction.strategy": "EDGE_LENGTH",
    "elk.layered.compaction.connectedComponents": "true",
    "elk.layered.mergeEdges": "true",
    "elk.edgeRouting": "ORTHOGONAL",
    "elk.layered.unnecessaryBendpoints": "true",
    // Pin nodes to layers by their data.level (see per-node partition below) so ELK's
    // crossing-minimisation works on the SAME rows the canvas displays (y is forced by
    // level downstream). Higher thoroughness = fewer crossings.
    "elk.partitioning.activate": "true",
    "elk.layered.thoroughness": "100",
    "elk.padding": "[top=20,left=20,bottom=20,right=20]",
  };

  const isArrange = groupBy.endsWith("-arrange");

  // Per-node real footprint (varies by node type / resized image size / label).
  const sizeMap = new Map();
  for (const n of nodes) {
    sizeMap.set(n.id, measureNode(n, nodeW, nodeH));
  }

  let elkGraph;

  if (isArrange) {
    const groupField = groupBy.startsWith("location") ? "locationName" : "level";
    const groups = new Map();
    for (const n of nodes) {
      const key = String(n.data?.[groupField] || "Ungrouped");
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(n);
    }

    const groupChildren = [];
    for (const [groupKey, groupNodes] of groups) {
      groupChildren.push({
        id: `__group__${groupKey}`,
        layoutOptions: {
          "elk.algorithm": "layered",
          "elk.direction": elkDirection,
          "elk.layered.spacing.nodeNodeBetweenLayers": String(Math.max(15, layerGap * 0.7)),
          "elk.spacing.nodeNode": String(Math.max(8, nodeGap * 0.8)),
          "elk.layered.nodePlacement.strategy": "NETWORK_SIMPLEX",
          "elk.layered.crossingMinimization.strategy": "LAYER_SWEEP",
          "elk.alignment": "CENTER",
          "elk.edgeRouting": "ORTHOGONAL",
          "elk.padding": `[top=${GROUP_PADDING + 15},left=${GROUP_PADDING},bottom=${GROUP_PADDING},right=${GROUP_PADDING}]`,
        },
        labels: [{ text: groupKey, width: groupKey.length * 8, height: 16 }],
        children: groupNodes.map((n) => {
          const s = sizeMap.get(n.id) || { w: nodeW, h: nodeH };
          return { id: n.id, width: s.w, height: s.h };
        }),
        edges: edges
          .filter((e) => {
            const srcGroup = nodes.find(nd => nd.id === e.source)?.data?.[groupField];
            const tgtGroup = nodes.find(nd => nd.id === e.target)?.data?.[groupField];
            return String(srcGroup || "Ungrouped") === groupKey && String(tgtGroup || "Ungrouped") === groupKey;
          })
          .map((e, i) => ({
            id: `e-int-${e.source}-${e.target}-${i}`,
            sources: [e.source],
            targets: [e.target],
          })),
      });
    }

    const crossEdges = edges
      .filter((e) => {
        const srcGroup = nodes.find(nd => nd.id === e.source)?.data?.[groupField];
        const tgtGroup = nodes.find(nd => nd.id === e.target)?.data?.[groupField];
        return String(srcGroup || "Ungrouped") !== String(tgtGroup || "Ungrouped");
      })
      .map((e, i) => ({
        id: `e-cross-${e.source}-${e.target}-${i}`,
        sources: [e.source],
        targets: [e.target],
      }));

    elkGraph = {
      id: "root",
      layoutOptions: {
        ...baseLayoutOptions,
        "elk.padding": "[top=20,left=20,bottom=20,right=20]",
        "elk.layered.spacing.nodeNodeBetweenLayers": String(Math.max(30, layerGap)),
        "elk.spacing.nodeNode": String(Math.max(20, nodeGap * 1.2)),
      },
      children: groupChildren,
      edges: crossEdges,
    };
  } else {
    elkGraph = {
      id: "root",
      layoutOptions: baseLayoutOptions,
      children: nodes.map((n) => {
        const s = sizeMap.get(n.id) || { w: nodeW, h: nodeH };
        const level = Number(n.data?.level);
        const child = { id: n.id, width: s.w, height: s.h };
        // Partition = layer = the node's hierarchy level, so ELK orders nodes within
        // each level to minimise crossings (matching the level-based rows we render).
        if (Number.isFinite(level)) child.layoutOptions = { "elk.partitioning.partition": String(level) };
        return child;
      }),
      edges: edges.map((e, i) => ({
        id: `e-${e.source}-${e.target}-${i}`,
        sources: [e.source],
        targets: [e.target],
      })),
    };
  }

  const layout = await elk.layout(elkGraph);

  const positionMap = new Map();
  const groupRects = [];

  if (isArrange) {
    for (const group of layout.children || []) {
      const gx = group.x || 0;
      const gy = group.y || 0;
      const label = group.id.replace("__group__", "");

      groupRects.push({
        id: group.id,
        label,
        x: gx,
        y: gy,
        width: group.width || 200,
        height: group.height || 200,
      });

      for (const child of group.children || []) {
        positionMap.set(child.id, {
          x: gx + (child.x || 0),
          y: gy + (child.y || 0),
        });
      }
    }
  } else {
    for (const child of layout.children || []) {
      positionMap.set(child.id, { x: child.x || 0, y: child.y || 0 });
    }
  }

  // A user-placed node (dropped from the palette and not yet wired up) keeps its
  // drop position so multiple drops don't stack. Everything else — connected nodes
  // and loaded-from-DB nodes — is positioned by ELK, which never overlaps.
  const connectedIds = new Set();
  edges.forEach(e => { connectedIds.add(e.source); connectedIds.add(e.target); });

  const layoutedNodes = nodes.map((n) => {
    const elkPos = positionMap.get(n.id) || { x: 0, y: 0 };
    const isConnected = connectedIds.has(n.id);
    const userPlaced = n.data?._userPlaced === true;
    const hasDropPos = n.position && (n.position.x !== 0 || n.position.y !== 0);
    const pos = (userPlaced && !isConnected && hasDropPos) ? n.position : elkPos;
    const s = sizeMap.get(n.id) || { w: nodeW, h: nodeH };
    return {
      ...n,
      position: { x: pos.x, y: pos.y },
      width: s.w,
      height: s.h,
      sourcePosition: "top",
      targetPosition: "bottom",
    };
  });

  const groupNodes = groupRects.map((g, i) => {
    const c = GROUP_COLOURS[i % GROUP_COLOURS.length];
    return {
      id: g.id,
      type: "group",
      position: { x: g.x, y: g.y },
      data: { label: g.label.toUpperCase(), labelColor: c.label },
      style: {
        width: g.width,
        height: g.height,
        backgroundColor: c.bg,
        border: `1px dashed ${c.border}`,
        borderRadius: 12,
        padding: 0,
        zIndex: -1,
      },
    };
  });

  // De-overlap as a final safety net (handles residual ELK / user-placed collisions).
  resolveOverlaps(layoutedNodes);

  return {
    nodes: layoutedNodes,
    edges,
    groupNodes,
    isArranged: isArrange,
  };
}
